import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogModule as MatDialogModule, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TransactionAttachment } from '../../../../models/entities/transaction-attachment';
import { TransactionsService } from '../../../../services/accounting/transactions.service';
import { GrowlerService } from '../../../shared/growler/growler.service';
import { ButtonModule } from '../../../shared/button/button.module';

export interface TransactionAttachmentPreviewDialogInput
{
    attachment: TransactionAttachment;
    transactionId: number;
}

@UntilDestroy()
@Component({
    selector: 'app-transaction-attachment-preview-dialog',
    standalone: true,
    templateUrl: './transaction-attachment-preview-dialog.component.html',
    styleUrls: ['./transaction-attachment-preview-dialog.component.scss'],
    imports: [
        CommonModule,
        MatDialogModule,
        PdfViewerModule,
        ButtonModule,
    ],
})
export class TransactionAttachmentPreviewDialogComponent implements OnInit, OnDestroy
{
    readonly containerHeight = '70vh';
    fileLoading = false;
    isDownloading = false;
    pdfSrc: ArrayBuffer;
    imgSrc: SafeUrl;

    private objectUrls: string[] = [];

    constructor(
            @Inject(MAT_DIALOG_DATA) public data: TransactionAttachmentPreviewDialogInput,
            private _dialogRef: MatDialogRef<TransactionAttachmentPreviewDialogComponent>,
            private _transactionsService: TransactionsService,
            private _growler: GrowlerService,
            private _sanitizer: DomSanitizer,
    ) {}

    ngOnInit(): void {
        const attachment = this.data.attachment;

        if (attachment.isPdf) {
            this.fileLoading = true;
            this._transactionsService.getAttachmentContents(this.data.transactionId, attachment.id, 'arraybuffer')
                    .pipe(untilDestroyed(this))
                    .subscribe({
                        next: (data) => {
                            this.pdfSrc = data as ArrayBuffer;
                            this.fileLoading = false;
                        },
                        error: (err: HttpErrorResponse) => {
                            this._growler.oops(this.retrievalErrorMessage(err, 'There was an error retrieving this file.'));
                            this.fileLoading = false;
                        },
                    });
        } else if (attachment.isImage) {
            this.fileLoading = true;
            this._transactionsService.getAttachmentContents(this.data.transactionId, attachment.id, 'blob')
                    .pipe(untilDestroyed(this))
                    .subscribe({
                        next: (data) => {
                            const objectUrl = window.URL.createObjectURL(data as Blob);
                            this.objectUrls.push(objectUrl);
                            this.imgSrc = this._sanitizer.bypassSecurityTrustUrl(objectUrl);
                            this.fileLoading = false;
                        },
                        error: (err: HttpErrorResponse) => {
                            this._growler.oops(this.retrievalErrorMessage(err, 'There was an error retrieving this file.'));
                            this.fileLoading = false;
                        },
                    });
        }
        // Unsupported types perform no fetch — the template renders the download-only card.
    }

    download(): void {
        if (this.isDownloading) {
            return;
        }

        this.isDownloading = true;

        this._transactionsService.getAttachmentContents(this.data.transactionId, this.data.attachment.id, 'blob')
                .pipe(untilDestroyed(this))
                .subscribe({
                    next: (data) => {
                        const objectUrl = window.URL.createObjectURL(data as Blob);
                        const link = document.createElement('a');
                        link.href = objectUrl;
                        link.download = this.data.attachment.filename;
                        link.click();
                        window.URL.revokeObjectURL(objectUrl);
                        this.isDownloading = false;
                    },
                    error: (err: HttpErrorResponse) => {
                        this._growler.oops(this.retrievalErrorMessage(err, 'There was a problem downloading the file.'));
                        this.isDownloading = false;
                    },
                });
    }

    /**
     * SD-8834: tell the member their file is GONE rather than that something went wrong.
     *
     * core answers 404 when the attachment row outlived its stored object (CORE-19A) — the member
     * clicked their own attachment and there is nothing to send. Every other failure keeps the
     * existing generic copy, because those ARE "something went wrong" and are still being paged on.
     *
     * Keyed on the STATUS, not on `err.error.message` as most of this app does: these three requests
     * use responseType 'arraybuffer'/'blob', so Angular leaves HttpErrorResponse.error as an
     * ArrayBuffer/Blob rather than parsed JSON and `.message` is undefined. Reading the body would
     * mean decoding it asynchronously in three places to recover a string we already know.
     */
    private retrievalErrorMessage(err: HttpErrorResponse, fallback: string): string {
        return err?.status === 404
            ? 'This attachment is no longer available.'
            : fallback;
    }

    close(): void {
        this._dialogRef.close();
    }

    ngOnDestroy(): void {
        this.objectUrls.forEach((url) => window.URL.revokeObjectURL(url));
    }
}
